import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useTenantConfig } from "@/context/TenantConfigContext";
import { companyLogo, divisionLogo } from "@/lib/brand";

/** The company's logo (MADIO's wordmark for MADIO), or its letter mark and name when it has none.
 * `className` sizes the image (set a height); `showName` adds the name beside the letter mark. */
export function CompanyLogo({ className = "h-6", showName = false, nameClass = "" }) {
  const { tenant } = useAuth();
  const [broken, setBroken] = useState(false);
  const src = companyLogo(tenant);
  const name = tenant?.display_name && tenant.display_name !== "MADIO CRM" ? tenant.display_name : tenant?.name || "MADIO";
  if (src && !broken) {
    return <img src={src} alt={name} className={`${className} w-auto max-w-[11rem] object-contain`} onError={() => setBroken(true)} data-testid="company-logo" />;
  }
  return (
    <span className="flex items-center gap-2">
      <span className="w-8 h-8 rounded-[var(--radius-md)] bg-[var(--color-navy)] text-white grid place-items-center font-bold text-sm shrink-0" aria-hidden="true">
        {name.slice(0, 1).toUpperCase()}
      </span>
      {showName && <span className={nameClass}>{name}</span>}
    </span>
  );
}

/** A division's logo (Furniture / MAP / D&W) where the company has one; otherwise nothing, or the
 * division's name when `fallback` is set. */
export function DivisionLogo({ division, className = "h-6", fallback = false }) {
  const { tenant } = useAuth();
  const { divisions } = useTenantConfig();
  const [broken, setBroken] = useState(false);
  const src = divisionLogo(tenant, division, divisions);
  if (!src || broken) {
    return fallback && division ? <span className="text-xs font-semibold text-[var(--color-text-muted)]">{division}</span> : null;
  }
  return <img src={src} alt={`${division} logo`} title={division} className={`${className} w-auto object-contain`} onError={() => setBroken(true)}
              data-testid={`division-logo-${division}`} />;
}
